/**
 * Token Handler Utility
 * Handles token extraction from URL parameters when loaded in Optix iframe
 * Also supports secure token transfer via localStorage using session IDs
 * 
 * Expected URL format: /?token=TOKEN (legacy) or /?sessionId=SESSION_ID (secure)
 * Example: https://example.com/?token=abc123xyz
 * Example: https://example.com/?sessionId=abc123xyz
 */

const SESSION_STORAGE_PREFIX = 'dashboard_token_session_'
const SESSION_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes
const FULLSCREEN_TOKEN_KEY = 'dashboard_fullscreen_token' // For persisting token across refreshes

/**
 * Stores a token in localStorage with a temporary session ID
 * @param {string} token - The token to store
 * @returns {string} The session ID
 */
export function storeTokenForSession(token) {
  if (!token) {
    console.error('storeTokenForSession: No token provided')
    return null
  }
  
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
  const storageKey = SESSION_STORAGE_PREFIX + sessionId
  
  // Store token with expiry timestamp
  const data = {
    token,
    expiresAt: Date.now() + SESSION_EXPIRY_MS
  }
  
  try {
    localStorage.setItem(storageKey, JSON.stringify(data))
    
    // Verify it was stored
    const verify = localStorage.getItem(storageKey)
    if (!verify) {
      console.error('Token storage verification failed - item not found after storing')
      return null
    }
    
    // Clean up expired sessions (but don't remove the one we just created)
    cleanupExpiredSessions(storageKey)
    
    return sessionId
  } catch (error) {
    console.error('Failed to store token in localStorage:', error)
    // Check if localStorage is available
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded')
    } else if (error.name === 'SecurityError') {
      console.error('localStorage access denied (SecurityError)')
    }
    return null
  }
}

/**
 * Retrieves a token from localStorage using a session ID
 * Also stores it in sessionStorage for persistence across page refreshes
 * Automatically cleans up the localStorage session after retrieval
 * @param {string} sessionId - The session ID
 * @returns {string|null} The token if found and valid, null otherwise
 */
export function getTokenFromSession(sessionId) {
  if (!sessionId) {
    console.warn('getTokenFromSession: No sessionId provided')
    return null
  }
  
  const storageKey = SESSION_STORAGE_PREFIX + sessionId
  
  try {
    const dataStr = localStorage.getItem(storageKey)
    if (!dataStr) {
      console.warn(`getTokenFromSession: No data found for key: ${storageKey}`)
      // Debug: list all localStorage keys with our prefix
      const allKeys = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(SESSION_STORAGE_PREFIX)) {
          allKeys.push(key)
        }
      }
      return null
    }
    
    const data = JSON.parse(dataStr)
    
    // Check if session has expired
    if (Date.now() > data.expiresAt) {
      console.warn('getTokenFromSession: Session expired')
      localStorage.removeItem(storageKey)
      return null
    }
    
    const token = data.token || null
    
    if (!token) {
      console.warn('getTokenFromSession: No token in session data')
      localStorage.removeItem(storageKey)
      return null
    }
    
    // Store token in sessionStorage for persistence across page refreshes
    // sessionStorage persists until the tab is closed, perfect for TV displays
    try {
      sessionStorage.setItem(FULLSCREEN_TOKEN_KEY, token)
    } catch (e) {
      console.warn('Failed to store token in sessionStorage:', e)
      // Continue anyway - token will still work for this page load
    }
    
    // Remove the localStorage session after retrieval for security
    localStorage.removeItem(storageKey)
    
    return token
  } catch (error) {
    console.error('Failed to retrieve token from localStorage:', error)
    // Clean up on error
    try {
      localStorage.removeItem(storageKey)
    } catch (e) {
      // Ignore cleanup errors
    }
    return null
  }
}

/**
 * Gets the token from sessionStorage (for page refreshes)
 * @returns {string|null} The token if found, null otherwise
 */
export function getTokenFromSessionStorage() {
  try {
    return sessionStorage.getItem(FULLSCREEN_TOKEN_KEY) || null
  } catch (error) {
    console.warn('Failed to get token from sessionStorage:', error)
    return null
  }
}

/**
 * Cleans up expired sessions from localStorage
 * @param {string} excludeKey - Optional key to exclude from cleanup (e.g., newly created session)
 */
function cleanupExpiredSessions(excludeKey = null) {
  try {
    const now = Date.now()
    const keysToRemove = []
    
    // Check all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(SESSION_STORAGE_PREFIX)) {
        // Skip the excluded key (newly created session)
        if (excludeKey && key === excludeKey) {
          continue
        }
        
        try {
          const dataStr = localStorage.getItem(key)
          if (dataStr) {
            const data = JSON.parse(dataStr)
            if (now > data.expiresAt) {
              keysToRemove.push(key)
            }
          }
        } catch (e) {
          // If we can't parse it, remove it
          keysToRemove.push(key)
        }
      }
    }
    
    // Remove expired keys
    if (keysToRemove.length > 0) {
      keysToRemove.forEach(key => localStorage.removeItem(key))
    }
  } catch (error) {
    // Silently fail cleanup
    console.warn('Failed to cleanup expired sessions:', error)
  }
}

// Store token received via postMessage
let tokenFromPostMessage = null

/**
 * Sets the token received via postMessage
 * @param {string} token - The token received
 */
export function setTokenFromPostMessage(token) {
  tokenFromPostMessage = token
  // Also store in sessionStorage for page refreshes
  if (token) {
    try {
      sessionStorage.setItem(FULLSCREEN_TOKEN_KEY, token)
    } catch (e) {
      console.warn('Failed to store token in sessionStorage:', e)
    }
  }
}

/**
 * Extracts the Optix API token from URL query parameters or session storage
 * Handles format: /?token=TOKEN (legacy) or /?sessionId=SESSION_ID (secure)
 * Also checks sessionStorage for tokens from previous page loads (for TV displays that refresh)
 * Also checks for token received via postMessage
 * @returns {string|null} The token if found, null otherwise
 */
export function getTokenFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  
  // First, check if we received token via postMessage (most reliable)
  if (tokenFromPostMessage) {
    return tokenFromPostMessage
  }
  
  // Second, check if we're in fullscreen mode and have a token in sessionStorage
  // This handles page refreshes on TV displays
  const fullscreenMode = urlParams.get('fullscreen') === 'true'
  if (fullscreenMode) {
    const sessionToken = getTokenFromSessionStorage()
    if (sessionToken) {
      return sessionToken
    }
  }
  
  // Third, try to get token from session ID (secure method for initial load)
  const sessionId = urlParams.get('sessionId')
  if (sessionId) {
    // Try multiple times with delays (localStorage might not be immediately available)
    const token = getTokenFromSession(sessionId)
    if (token) {
      return token
    } else {
      // Retry after a short delay (localStorage might not be synced yet)
      setTimeout(() => {
        const retryToken = getTokenFromSession(sessionId)
        if (retryToken) {
          // Trigger a re-validation if needed
          window.dispatchEvent(new Event('token-retrieved'))
        }
      }, 100)
      console.warn('Failed to retrieve token from sessionId, will retry')
    }
  }
  
  // Fallback to legacy method: token in URL (for backward compatibility)
  const token = urlParams.get('token')
  
  // Only log warning if we truly have no token and no sessionId
  if (!token && !sessionId) {
    console.warn('Token not found in URL. Current URL:', window.location.href)
  }
  
  return token || null
}

/**
 * Checks if trial mode is enabled via URL query parameters
 * Supports: ?trial=true, ?trial=1, ?trial_mode=true, ?trial_mode=1
 * @returns {boolean} True if trial mode should be enabled from URL
 */
export function getTrialModeFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  const trial = urlParams.get('trial') ?? urlParams.get('trial_mode')
  if (!trial) return false
  const lower = String(trial).toLowerCase()
  return lower === 'true' || lower === '1' || lower === 'yes'
}

/**
 * Extracts organization ID from URL query parameters
 * @returns {string|null} The organization ID if found, null otherwise
 */
export function getOrganizationIdFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('orgId') || urlParams.get('organization_id') || null
}


/**
 * Validates that required parameters are present
 * Only token is required - organization_id can be fetched from API
 * Supports both legacy token in URL and secure sessionId method
 * @returns {Object} Object with isValid flag and missing parameters
 */
export function validateIframeParams() {
  const token = getTokenFromURL()
  const orgId = getOrganizationIdFromURL()
  
  const missing = []
  if (!token) missing.push('token')
  // organization_id is optional - can be fetched from API
  
  return {
    isValid: missing.length === 0,
    missing,
    token,
    orgId // May be null if not in URL
  }
}
